import React, { useState, useEffect, useCallback, useRef } from "react";
import { db } from "../firebase";
import {
  collection,
  doc,
  getDocs,
  where,
  updateDoc,
  query,
  serverTimestamp,
} from "firebase/firestore/lite";
import {
  Button,
  Typography,
  Container,
  Grid,
  Card,
  FormControlLabel,
  Radio,
  Checkbox,
  TextField,
} from "@mui/material";
import MySpinner from "./MySpinner";
import SoftBox from "./SoftBox";
import SoftTypography from "./SoftTypography";
import SoftButton from "./SoftButton";
import TagManager from "react-gtm-module";
import {
  BEGIN_TEST,
  FINISH,
  LISTENING_TEST,
  READING_TEST,
  VOCAB_TEST,
  VOCABULARY,
  APT_TEST,
  COMPREHENSIVE_TEST,
  EMOTIONAL_INTELLIGENCE_TEST,
  CALL_CENTER_KNOWLEDGE_TEST,
  CUSTOMER_SERVICE_TEST,
  SALES_TEST,
  SALES_RETENTION_TEST,
  ATTENTION_TO_DETAIL_TEST,
  CEFR_READING_TEST,
  CEFR_LISTENING_TEST,
  CEFR_WRITING_TEST,
  TECH_SUPPORT,
  AudioWrapper,
  ATTENDANCE_TEST,
  INTEGRITY_TEST,
  APTITUDE_TECHNICAL_TEST,
} from "./helper";

export default function GrammerAssessment({
  assessment,
  handleNextStep,
  region,
  questions,
  category,
  test,
  nextSectionName,
}) {
  const [loadingVocab, setLoadingVocab] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [vocabAnswers, setVocabAnswers] = useState([]);
  const [submitScores, setSubmitScores] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveError, setSaveError] = useState("");   // CDT-309

  // Calculate total number of questions
  const [questionLength, setQuestionLength] = useState(0);
  const [subQuestionLength, setSubQuestionLength] = useState(0);
  const [wordLimitExceeded, setWordLimitExceeded] = useState([]);

  const currentAudioRef = useRef(null);

  // --- audio playback diagnostics -----------------------------------------
  // Many corporate networks block or throttle the static media host, which the
  // candidate experiences as "the audio just doesn't play". Record what
  // actually happened so it is visible in the record instead of being
  // reconstructed from a support ticket afterwards.
  const MEDIA_ERR = {
    1: "ABORTED — playback cancelled",
    2: "NETWORK — request blocked or connection dropped",
    3: "DECODE — file arrived but could not be decoded",
    4: "SRC_NOT_SUPPORTED — blocked, 404, or wrong content type",
  };

  const logAudioEvent = (audioUrl, event, extra = {}) => {
    if (!assessment?.uid) return;
    const key = `audio_logs.${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    updateDoc(doc(collection(db, "assessments"), assessment.uid), {
      [key]: {
        event,
        audioUrl,
        host: process.env.REACT_APP_STATIC_REPO || "",
        step: category || "unknown",
        at: new Date().toISOString(),
        ...extra,
      },
    }).catch((e) => console.error("audio log failed:", e));
  };

  const handleAudioError = (e, audioUrl) => {
    const err = e?.target?.error;
    const code = err?.code;
    logAudioEvent(audioUrl, "error", {
      code: code ?? null,
      reason: MEDIA_ERR[code] || "unknown media error",
      networkState: e?.target?.networkState ?? null,
      readyState: e?.target?.readyState ?? null,
      // strongest signal that a proxy ate it rather than the file being bad
      likelyNetworkBlock: code === 2 || code === 4,
    });
  };

  const audioStartRef = useRef({});

  const handleAudioLoadStart = (audioUrl) => {
    audioStartRef.current[audioUrl] = performance.now();
  };

  /**
   * Read what the browser actually measured for this file.
   *
   * The earlier version timed loadstart -> loadedmetadata, which is NOT
   * download time: with no preload the browser may not fetch anything until
   * the candidate presses play, so that number was partly "how long they took
   * to click". Resource Timing gives the real network figures.
   *
   * Cross-origin caveat: without a Timing-Allow-Origin header on the static
   * host, sizes and phase breakdowns come back as 0 and only duration is
   * usable. We record that so the data is never read as "0 bytes transferred".
   */
  const readResourceTiming = (audioUrl) => {
    try {
      if (!performance?.getEntriesByName) return null;
      const entries = performance.getEntriesByName(audioUrl, "resource");
      if (!entries.length) return null;
      const e = entries[entries.length - 1];

      // transferSize is only exposed to us when the host allows it.
      const detailsVisible = e.transferSize > 0 || e.decodedBodySize > 0;

      return {
        durationMs: Math.round(e.duration),
        ttfbMs: e.responseStart ? Math.round(e.responseStart - e.requestStart) : null,
        transferBytes: e.transferSize || null,
        decodedBytes: e.decodedBodySize || null,
        // transferSize 0 with a real body means it was served from cache
        fromCache: detailsVisible && e.transferSize === 0 && e.decodedBodySize > 0,
        timingVisible: detailsVisible,
      };
    } catch (err) {
      return null;
    }
  };

  /**
   * Warm the HTTP cache for this section's audio as soon as the section opens.
   *
   * The <audio> elements carry preload="none" so iOS does not block on them,
   * which means nothing is downloaded until the candidate presses play — and
   * then they wait. Fetching in the background here means the file is already
   * in cache by the time they press it.
   *
   * fetch() is not subject to the iOS user-gesture restriction that applies to
   * media elements, so this warms the cache on iPhone and iPad too.
   *
   * Entirely best-effort: failures are ignored and nothing blocks the page.
   */
  useEffect(() => {
    if (!questions?.length) return;

    const base = process.env.REACT_APP_STATIC_REPO || "";
    const urls = [];
    questions.forEach((q) => {
      if (q?.audio_url) urls.push(`${base}${q.audio_url}`);
      (q?.questions || []).forEach((sub) => {
        if (sub?.audio_url) urls.push(`${base}${sub.audio_url}`);
      });
    });

    const unique = [...new Set(urls)];
    if (!unique.length) return;

    const controller = new AbortController();
    unique.forEach((url) => {
      const started = performance.now();
      fetch(url, { signal: controller.signal, mode: "cors", cache: "default" })
        .then((r) => r.blob())
        .then(() => {
          const ms = Math.round(performance.now() - started);
          if (ms > 5000) {
            logAudioEvent(url, "slow_prefetch", { ms, ...(readResourceTiming(url) || {}) });
          }
        })
        .catch(() => {
          /* offline, aborted, or CORS — the player will try again on press */
        });
    });

    return () => controller.abort();
  }, [questions]);

  const handleAudioLoaded = (audioUrl) => {
    const t0 = audioStartRef.current[audioUrl];
    delete audioStartRef.current[audioUrl];

    const timing = readResourceTiming(audioUrl);
    // Time from the element starting to load until metadata was ready. Kept
    // for comparison, but it is NOT download time — see readResourceTiming.
    const elementMs = t0 ? Math.round(performance.now() - t0) : null;

    // Judge "slow" on the real network duration where we have it.
    const networkMs = timing?.durationMs ?? null;
    const slow = networkMs !== null ? networkMs > 5000 : (elementMs ?? 0) > 15000;
    if (!slow) return;

    logAudioEvent(audioUrl, "slow_load", {
      networkMs,
      elementMs,
      ...(timing || { timingUnavailable: true }),
    });
  };

  const handleAudioPlay = (e) => {
    if (currentAudioRef.current && currentAudioRef.current !== e.target) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
    }
    currentAudioRef.current = e.target;
  };
  // const handleAudioPlay = (event) => {
  //   const allAudios = document.querySelectorAll("audio");
  //   allAudios.forEach((audio) => {
  //     if (audio !== event.target) {
  //       audio.pause();
  //     }
  //   });
  // };

  useEffect(() => {
    let totalQuestions = 0;
    if (questions) {
      questions.forEach((item) => {
        if (item.questions) {
          // If the item is of type SHORT_PASSAGE, count the questions inside the "questions" array
          totalQuestions += item.questions.length;
        } else {
          // Otherwise, count the current question
          totalQuestions += 1;
        }
      });
      setQuestionLength(totalQuestions);
      if (questions && questionLength) {
        setSelectedAnswers(Array(questionLength).fill(""));
      }
    }
  }, [questions]);

  const handleAnswer = (e, value, index, sub_index) => {
    const newAnswers = [...selectedAnswers];

    // Initialize sub-array for the question if it doesn't exist
    if (!newAnswers[index]) {
      newAnswers[index] = [];
    }
    // If sub_index is defined (not undefined or null), update the sub-array for the specific sub-question
    if (sub_index !== undefined && sub_index !== null) {
      // Include the answer value
      newAnswers[index][sub_index] = value;
    } else {
      // Otherwise, update the main question (no sub-question)
      newAnswers[index] = value;
    }

    setSelectedAnswers(newAnswers);
  };

  const handleTextAnswer = (e, index, wordLimit) => {
    const updatedAnswers = [...selectedAnswers];
    updatedAnswers[index] = e.target.value;
    setSelectedAnswers(updatedAnswers);

    if (wordLimit) {
      const wordCount = e.target.value.trim().split(/\s+/).filter(Boolean).length;
      const updatedExceededLimits = [...wordLimitExceeded];
      updatedExceededLimits[index] = wordCount > wordLimit;
      setWordLimitExceeded(updatedExceededLimits);
    }
  };

  const handleNextStep1 = useCallback(() => {
    handleNextStep();
  }, []);

  useEffect(() => {
    TagManager.dataLayer({
      dataLayer: {
        event: "component_loaded",
        component: "GrammerAssessment",
        questions: questions,
      },
    });
  }, []);

  useEffect(() => {
    const updateGrammarScores = async () => {
      if (
        !submitScores ||
        isSubmitting ||
        !assessment?.uid ||
        !vocabAnswers?.length
      ) {
        return;
      }

      setIsSubmitting(true);
      setSaveError("");

      // CDT-309: send the step we are answering. Without it the backend falls
      // back to its own stored currentStep, which can drift from the browser's.
      const requestBody = {
        assessment_uid: assessment.uid,
        answers: vocabAnswers,
        current_step: category,
      };

      // CDT-309: a lost save used to be silent — logged to the console and
      // nothing else — so a candidate could finish the test with a section
      // never stored. Retry, and only advance once the server confirms.
      const MAX_ATTEMPTS = 3;
      let saved = false;
      let lastProblem = "";
      let serverMessage = "";

      for (let attempt = 1; attempt <= MAX_ATTEMPTS && !saved; attempt++) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_SERVER_URL}/update_grammar_scores`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify(requestBody),
            }
          );

          if (response.ok) {
            saved = true;
            break;
          }

          lastProblem = `HTTP ${response.status}`;
          console.error("Failed to update grammar scores:", response.statusText);

          // 4xx will not succeed on a retry — stop and surface it.
          // The server explains what is actually wrong, and it is not always
          // the connection: an empty section comes back as 400 with a message
          // asking for an answer. Telling her to check her internet instead
          // would leave her pressing Next forever.
          if (response.status >= 400 && response.status < 500) {
            try {
              const body = await response.json();
              if (body && body.error) serverMessage = body.error;
            } catch (e) {
              // No JSON body — fall back to the generic wording below.
            }
            break;
          }
        } catch (error) {
          lastProblem = error.message || "network error";
          console.error("Error occurred during API call:", error);
        }

        if (attempt < MAX_ATTEMPTS) {
          await new Promise((r) => setTimeout(r, attempt * 1500));
        }
      }

      if (saved) {
        handleNextStep1();
      } else {
        // Answers stay on screen so nothing is lost — she can press Next again.
        setSaveError(
          serverMessage ||
          `We could not save your answers (${lastProblem}). Your responses are still here — ` +
          `please check your internet connection and press Next again.`
        );
      }

      setIsSubmitting(false);
      setSubmitScores(false);
    };

    updateGrammarScores();
  }, [submitScores, assessment, vocabAnswers, handleNextStep1]);

  const submit = () => {
    // Prevent submission if any FREE_WRITE response exceeds the limit
    if (wordLimitExceeded.some(exceeded => exceeded)) {
        alert("One or more responses exceed the allowed word limit. Please shorten them before submitting.");
        return;
    }

    const newVocabAnswers = questions.map((question, index) => {
      const {
        question: questionText,
        correct_answer,
        difficulty,
        category,
      } = question;
      const answer = selectedAnswers[index];
      return {
        question: questionText,
        answer,
        difficulty,
        category,
        correct_answer,
      };
    });
    
    const newVocabAnswers1 = questions.map((question, index) => selectedAnswers[index]);
    setVocabAnswers(newVocabAnswers1);
    setSubmitScores(true);
};
  const section_header =
    category === VOCAB_TEST
      ? "Vocabulary and Grammar"
      : category === ATTENDANCE_TEST
      ? "Attendance"
      : category === INTEGRITY_TEST
      ? "Integrity"
      : category === VOCABULARY
      ? "Vocabulary and Grammar"
      : category === APT_TEST
      ? "Apptitude w/Technical"
      : category === APTITUDE_TECHNICAL_TEST
      ? "Apptitude & Technical"
      : category === COMPREHENSIVE_TEST
      ? "Comprehension"
      : category === EMOTIONAL_INTELLIGENCE_TEST
      ? "Emotional Intelligence"
      : category === CALL_CENTER_KNOWLEDGE_TEST
      ? "Call Center Knowledge"
      : category === CUSTOMER_SERVICE_TEST
      ? "Customer Service"
      : category === TECH_SUPPORT
      ? "Technical Proficiency"
      : category === SALES_TEST
      ? "Sales"
      : category === SALES_RETENTION_TEST 
      ? "Sales & Retention" 
      : category === ATTENTION_TO_DETAIL_TEST
      ? "Attention to Detail"
      : category === LISTENING_TEST
      ? "Listening"
      : category === 'attendance_es'
      ? "Asistencia"
      : [];

  let globalIndex = 0; // To keep track of the global question count

  const renderGrammer = () => {
    if (loadingVocab || !questions) {
      return (
        <div className="spinner">
          <MySpinner />
        </div>
      );
    } else {
      const sections = region?.tests[test].sections;
      const currentSection = sections.find(section => section.field === category);

      return (
        <>
          <Container sx={{backgroundColor: "#FFFFFF", borderRadius: "0px", padding: "0px !important", margin: "-30px auto 20px auto !important"}}>
            <div style={{backgroundColor: "#FFFFFF", padding: "0 20px 5px 20px"}}>
              <SoftBox mt={1} mb={1} style={{backgroundColor: "#FFFFFF"}}>
                <SoftTypography variant="body1" fontWeight="bold" color="dark">
                  <div style={{ display: "flex" }}>
                    <p style={{ flex: "0.6", fontSize: "24px", fontWeight: "700", color: "rgba(79, 79, 79, 1)" }}>{currentSection?.heading ? currentSection.heading : "Please set the header"}</p>
                  </div>
                </SoftTypography>
              </SoftBox>
              <SoftBox mb={1} style={{backgroundColor: "#FFFFFF"}}>
                <SoftTypography variant="body2" fontWeight="medium" color="dark" style={{color: "rgba(132, 132, 132, 1)", fontSize: "16px", fontWeight: "400"}}>
                  {currentSection?.sub_heading ? currentSection.sub_heading : "Choose the correct answer"}
                </SoftTypography>
              </SoftBox>
            </div>
          </Container>
          <Container sx={{borderRadius: "0px", padding: "0px !important"}}>
          <SoftBox>
              {questions.map((question, index) => {

                // Sub-Section Heading logic for CEFR
                let sectionDescription = "";
                let descriptionShown = false;  // Flag to track if the description is shown for each sub_section
                let freeWriteWordLimit = 0;
                if (sections) {
                  sections.forEach((section) => {

                    if (section.field === category) {
                      let subSections = section.sub_sections;

                      if (subSections) {
                        let cumulativeSize = 0;

                        // Loop through the sub_sections array
                        for (let i = 0; i < subSections.length; i++) {
                          cumulativeSize += subSections[i]?.size; // Add the current size to the cumulative sum

                          // Check if globalIndex matches the cumulative size for the first question in each sub_section
                          if (globalIndex === cumulativeSize - subSections[i]?.size && !descriptionShown) {
                            sectionDescription = subSections[i]?.description;
                            descriptionShown = true;
                            freeWriteWordLimit = subSections[i]?.word_limit;
                            break;
                          }
                          // Reset descriptionShown after the end of the sub_section range
                          if (globalIndex < cumulativeSize) {
                            descriptionShown = false;
                          }
                        }
                      }
                    }
                  });
                }
                return (
                  <>
                    {/* Render section description at the start of each new section */}
                    {sectionDescription && (
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: 700,
                          borderRadius: "8px",
                          color: "rgba(79, 79, 79, 1)",
                          marginBottom: "10px",
                          backgroundColor: "rgba(221, 225, 251, 1)",
                          padding: "12px 20px",
                        }}
                      >
                        {sectionDescription}
                      </div>
                    )}
                    
                    {question.type === "SHORT_PASSAGE" && (
                      <SoftBox
                        mb={1}
                        pl={1}
                        style={{
                          backgroundColor: "rgba(234, 234, 234, 1)",
                          padding: "5px 20px 0 20px",
                          borderRadius: "8px",
                        }}
                      >
                        <SoftTypography
                          variant="h6"
                          fontWeight="medium"
                          color="dark"
                          style={{
                            paddingBottom: question.img_url ? "0" : "8px",
                            color: "rgba(79, 79, 79, 1)",
                            fontSize: "16px",
                            fontWeight: "400",
                            textAlign: "left",
                          }}
                        >
                          <div
                            dangerouslySetInnerHTML={{
                              __html: question.paragraph_text,
                            }}
                          />
                        </SoftTypography>

                        {question.img_url && (
                          <div style={{ textAlign: "center", margin: "12px 0" }}>
                            <img
                              src={`${process.env.REACT_APP_STATIC_REPO}${question.img_url}`}
                              alt="Related Visual"
                              style={{
                                maxWidth: "100%",
                                height: "auto",
                                borderRadius: "4px",
                              }}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = "none";
                              }}
                            />
                          </div>
                        )}
                      </SoftBox>
                    )}
                    {question.type === "AUDIO" && (
                      // <AudioWrapper>
                      //   <audio
                      //     controls
                      //     style={{ width: "100%", margin: "1rem 0" }}
                      //     preload="metadata"          // preload metadata
                      //     onPlay={handleAudioPlay} // single play handler
                      //     // onPause={handlePause}
                      //     // onEnded={handleAudioEnd}
                      //   >
                          
                      //     <source src={process.env.REACT_APP_STATIC_REPO+ question.audio_url} type="audio/mp3" />
                      //   </audio>
                      <AudioWrapper>
                        <audio
                          controls
                          preload="none" // recommended for iOS to avoid blocking loads
                          style={{ width: "100%", margin: "1rem 0" }}
                          playsInline // needed for Safari on iOS
                          muted={false} // Safari sometimes requires initial muted state, but set false for manual play
                          onPlay={(e) => {
                            // Pause any other playing audio
                            const audios = document.querySelectorAll("audio");
                            audios.forEach((audio) => {
                              if (audio !== e.target) {
                                audio.pause();
                              }
                            });
                          }}
                          onError={(e) =>
                            handleAudioError(
                              e,
                              `${process.env.REACT_APP_STATIC_REPO}${question.audio_url}`
                            )
                          }
                          onLoadStart={() =>
                            handleAudioLoadStart(
                              `${process.env.REACT_APP_STATIC_REPO}${question.audio_url}`
                            )
                          }
                          onLoadedMetadata={() =>
                            handleAudioLoaded(
                              `${process.env.REACT_APP_STATIC_REPO}${question.audio_url}`
                            )
                          }
                        >
                          <source
                            src={`${process.env.REACT_APP_STATIC_REPO}${question.audio_url}`}
                            type={
                              question.audio_url.endsWith(".m4a")
                                ? "audio/mp4"
                                : "audio/mpeg"
                            }
                            // With <source> children the error event fires here,
                            // not on the <audio> element, so both are wired up.
                            onError={() =>
                              logAudioEvent(
                                `${process.env.REACT_APP_STATIC_REPO}${question.audio_url}`,
                                "source_error",
                                {
                                  reason:
                                    "source failed to load — blocked, 404, or wrong content type",
                                  likelyNetworkBlock: true,
                                }
                              )
                            }
                          />
                          Your browser does not support the audio element.
                        </audio>
                      </AudioWrapper>
                    )}

                    <Card
                      key={index}
                      mb={2}
                      variant="outlined"
                      style={{
                        marginBottom: "1rem",
                        paddingBottom: "1rem",
                        boxShadow: "none",
                        backgroundColor:
                          question.type === "SHORT_PASSAGE" ? "rgba(234, 234, 234, 1)" : "",
                      }}
                    >
                      <SoftBox lineHeight={0}>
                        {/* For SHORT READING question type, render the paragraph text & iterate through sub-questions */}
                        {question.type === "SHORT_PASSAGE" || question.type === "AUDIO" ? (
                          <>
                            {question.questions &&
                              question.questions.map((subQuestion, subIndex) => {
                                globalIndex++;

                                return (
                                  <>
                                    <Card
                                      key={index}
                                      mb={2}
                                      variant="outlined"
                                      style={{
                                        marginBottom: "1rem",
                                        paddingBottom: "1rem",
                                        boxShadow: "none"
                                      }}
                                    >
                                      <SoftBox lineHeight={0}>
                                        <SoftBox
                                          key={subIndex}
                                          mb={1}
                                          pl={1}
                                          style={{backgroundColor: "rgba(234, 234, 234, 1)", padding: "10px 10px 10px 20px"}}>
                                          <SoftTypography
                                            variant="h6"
                                            fontWeight="medium"
                                            color="dark"
                                            style={{ paddingBottom: "8px", color: "rgba(79, 79, 79, 1)", fontSize: "16px" }}
                                            >
                                            <div
                                              dangerouslySetInnerHTML={{
                                                __html: subQuestion.question,
                                              }}
                                            />
                                          </SoftTypography>
                                          <div
                                            style={{
                                              display: "flex",
                                              paddingTop: "5px",
                                              height: "15px",
                                              fontWeight: 400,
                                              fontSize: "1rem",
                                              marginTop: "2px",
                                              marginBottom: "4px",
                                              color: "rgba(79, 79, 79, 1)"
                                            }}
                                          >
                                            Q {globalIndex} of {questionLength}
                                          </div>
                                        </SoftBox>

                                        {/* Render options for this sub-question */}

                                        <SoftBox
                                          component="ul"
                                          display="flex"
                                          flexDirection="column"
                                          p={0}
                                          m={0}
                                          mt={0.25}
                                          pt={0.5}
                                          style={{margin: "0 10px"}}
                                        >
                                          {subQuestion.options &&
                                            subQuestion.options.map(
                                              (choice, choiceIndex) => (
                                                <SoftBox
                                                  key={choiceIndex}
                                                  component="li"
                                                  display="flex"
                                                  alignItems="center"
                                                  borderRadius="lg"
                                                  p={0.25}
                                                  px={2.5}
                                                  mb={0}
                                                >
                                                  <FormControlLabel
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                    }}
                                                    control={
                                                      subQuestion.type ===
                                                      "SINGLE_SELECT" ? (
                                                        <Radio
                                                          id={`answer-${index}-${subIndex}-${choiceIndex}`}
                                                          name={`answer-${index}-${subIndex}`}
                                                          value={choice}
                                                          checked={
                                                            selectedAnswers[
                                                              index
                                                            ]?.[subIndex] ===
                                                            choice
                                                          }
                                                          onChange={(e) =>
                                                            handleAnswer(
                                                              e,
                                                              choice,
                                                              index,
                                                              subIndex
                                                            )
                                                          }
                                                          sx={{
                                                            color: "rgba(132, 132, 132, 1)",
                                                            "& .MuiSvgIcon-root": {
                                                              fontSize: "16px",
                                                            },
                                                            "&.Mui-checked": {
                                                              backgroundImage: `url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgdmlld0JveD0iMCAwIDE2IDE2IiBmaWxsPSJub25lIj4KICAgPHJlY3QgeD0iMC41IiB5PSIwLjUiIHdpZHRoPSIxNSIgaGVpZ2h0PSIxNSIgcng9IjcuNSIgZmlsbD0iI0VFRjBGRCIvPgogICA8cmVjdCB4PSIwLjUiIHk9IjAuNSIgd2lkdGg9IjE1IiBoZWlnaHQ9IjE1IiByeD0iNy41IiBzdHJva2U9IiM1NTY5RUQiLz4KICAgPGNpcmNsZSBjeD0iOCIgY3k9IjgiIHI9IjMiIGZpbGw9IiM1NTY5RUQiLz4KPC9zdmc+') !important`,
                                                              backgroundRepeat: "no-repeat",
                                                              backgroundPosition: "center",
                                                              backgroundSize: "contain",
                                                            },
                                                            "&.MuiRadio-root": {
                                                              height: "16px",
                                                              width: "16px",
                                                              border: "1px solid rgba(132, 132, 132, 1)",
                                                              backgroundColor: "rgba(243, 243, 243, 1)",
                                                            },
                                                          }}
                                                        />
                                                      ) : (
                                                        <></> // Handle MULTI_SELECT if needed
                                                      )
                                                    }
                                                    label={
                                                      <SoftTypography
                                                        variant="body2"
                                                        fontWeight="medium"
                                                        color="dark"
                                                        ml={0.5}
                                                        style={{ color: "rgba(79, 79, 79, 1)", fontSize: "16px", fontWeight: "400" }}
                                                      >
                                                        {choice}
                                                      </SoftTypography>
                                                    }
                                                  />
                                                </SoftBox>
                                              )
                                            )}
                                        </SoftBox>
                                      </SoftBox>
                                    </Card>
                                  </>
                                );
                              })}
                          </>
                        ) : (
                          // For regular questions, render the question with a number
                          <>
                            <div style={{display:'none'}}>{globalIndex++}</div>
                            <SoftBox mb={1} pl={1} style={{backgroundColor: "rgba(234, 234, 234, 1)", padding: "10px 10px 10px 20px"}}>
                              <SoftTypography
                                variant="h6"
                                fontWeight="medium"
                                color="dark"
                                style={{ paddingBottom: "8px", color: "rgba(79, 79, 79, 1)", fontSize: "16px" }}
                              >
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: question.question,
                                  }}
                                />
                              </SoftTypography>
                              {question.type === "FREE_WRITE" && (
                                <SoftTypography
                                  variant="h6"
                                  fontWeight="medium"
                                  color="dark"
                                  style={{ paddingBottom: "8px", color: "rgba(79, 79, 79, 1)", fontSize: "16px", fontWeight: 400 }}
                                  >
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: `
                                        <style>
                                          ul {
                                            list-style-type: disc;
                                            padding-left: 20px;
                                          }
                                          li {
                                            margin-bottom: 5px;
                                          }
                                        </style>
                                        ${question.description}
                                      `,
                                    }}
                                  />
                                </SoftTypography>
                              )}

                              <div
                                style={{
                                  display: "flex",
                                  paddingTop: "5px",
                                  height: "15px",
                                  fontWeight: 400,
                                  fontSize: "1rem",
                                  marginTop: "2px",
                                  marginBottom: "4px",
                                  color: "rgba(79, 79, 79, 1)"
                                }}
                              >
                                Q {globalIndex} of {questionLength}
                              </div>
                            </SoftBox>
                          </>
                        )}

                        {/* Render input field if the question is SCRAMBLED_TEXT */}
                        {(question.type === "SCRAMBLED_TEXT" || question.type === "FREE_WRITE") ? (
                          question.type === "FREE_WRITE" ? (
                            <SoftBox mt={1} px={2} sx={{ position: 'relative', width: '100%' }}>
                              <TextField
                                id={`answer-${index}`}
                                name={`answer-${index}`}
                                value={selectedAnswers[index] || ""}
                                onChange={(e) => handleTextAnswer(e, index, freeWriteWordLimit)}
                                onCopy={(e) => e.preventDefault()}
                                onCut={(e) => e.preventDefault()}
                                onPaste={(e) => e.preventDefault()}
                                fullWidth
                                variant="outlined"
                                placeholder="Type your response"
                                multiline
                                rows={10}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    width: '100% !important',
                                  },
                                  '& textarea': {
                                    width: '100% !important',
                                    padding: '10px',
                                    userSelect: 'text',
                                    WebkitUserSelect: 'text',
                                  },
                                  height: "210px"
                                }}
                              />
                              <SoftBox
                                display="flex"
                                justifyContent="flex-end"
                                sx={{
                                  position: 'absolute',
                                  bottom: '-5px',
                                  right: '30px',
                                  fontSize: '1rem !important',
                                  color: wordLimitExceeded[index] ? 'red' : 'gray', // Red if exceeded
                                  zIndex: 1,
                                }}
                              >
                                <SoftTypography variant="body2" color="textSecondary" style={{ fontSize: '1rem !important' }}>
                                  {(typeof selectedAnswers[index] === "string"
                                    ? selectedAnswers[index].trim().split(/\s+/).filter(Boolean).length
                                    : 0)} / {freeWriteWordLimit}
                                </SoftTypography>
                              </SoftBox>
                            </SoftBox>
                          ) : (
                            <SoftBox mt={1} px={2}>
                              <TextField
                                id={`answer-${index}`}
                                name={`answer-${index}`}
                                value={selectedAnswers[index] || ""}
                                onChange={(e) => handleTextAnswer(e, index)}
                                onCopy={(e) => e.preventDefault()}
                                onCut={(e) => e.preventDefault()}
                                onPaste={(e) => e.preventDefault()}
                                fullWidth
                                variant="outlined"
                                placeholder="Rearrange the sentence"
                                sx={{
                                  '& input': {
                                    userSelect: 'text',
                                    WebkitUserSelect: 'text',
                                  },
                                }}
                              />
                            </SoftBox>
                          )
                        ) : (
                          <SoftBox
                            component="ul"
                            display="flex"
                            flexDirection="column"
                            p={0}
                            m={0}
                            mt={0.25}
                            pt={0.5}
                            style={{margin: "0 10px"}}
                          >
                            {question.type !== "SHORT_PASSAGE" && question.type !== "AUDIO" &&
                              question.options.map((choice, choiceIndex) => (
                                <SoftBox
                                  key={choiceIndex}
                                  component="li"
                                  display="flex"
                                  alignItems="center"
                                  borderRadius="lg"
                                  p={0.25}
                                  px={2.5}
                                  mb={0}
                                >
                                  <FormControlLabel
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                    control={
                                      question.type === "SINGLE_SELECT" ? (
                                        <Radio
                                          id={`answer-${index}-${choiceIndex}`}
                                          name={`answer-${index}`}
                                          value={choice}
                                          checked={selectedAnswers[index] === choice}
                                          onChange={(e) => handleAnswer(e, choice, index)}
                                          sx={{
                                            color: "rgba(132, 132, 132, 1)",
                                            "& .MuiSvgIcon-root": {
                                              fontSize: "16px",
                                            },
                                            "&.Mui-checked": {
                                              backgroundImage: `url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgdmlld0JveD0iMCAwIDE2IDE2IiBmaWxsPSJub25lIj4KICAgPHJlY3QgeD0iMC41IiB5PSIwLjUiIHdpZHRoPSIxNSIgaGVpZ2h0PSIxNSIgcng9IjcuNSIgZmlsbD0iI0VFRjBGRCIvPgogICA8cmVjdCB4PSIwLjUiIHk9IjAuNSIgd2lkdGg9IjE1IiBoZWlnaHQ9IjE1IiByeD0iNy41IiBzdHJva2U9IiM1NTY5RUQiLz4KICAgPGNpcmNsZSBjeD0iOCIgY3k9IjgiIHI9IjMiIGZpbGw9IiM1NTY5RUQiLz4KPC9zdmc+') !important`,
                                              backgroundRepeat: "no-repeat",
                                              backgroundPosition: "center",
                                              backgroundSize: "contain",
                                            },
                                            "&.MuiRadio-root": {
                                              height: "16px",
                                              width: "16px",
                                              border: "1px solid rgba(132, 132, 132, 1)",
                                              backgroundColor: "rgba(243, 243, 243, 1)",
                                            },
                                          }}
                                        />
                                      ) : (
                                        <Checkbox
                                          id={`answer-${index}-${choiceIndex}`}
                                          name={`answer-${index}`}
                                          value={choice}
                                          checked={(selectedAnswers[index] || []).includes(choice)}
                                          onChange={(e) => handleMultiAnswer(e, choice, index)}
                                          sx={{
                                            "&.MuiCheckbox-root": {
                                              height: "18px",
                                              width: "18px",
                                            },
                                          }}
                                        />
                                      )
                                    }
                                    label={
                                      <SoftTypography
                                        variant="body2"
                                        fontWeight="medium"
                                        color="dark"
                                        ml={0.5}
                                      >
                                        {choice}
                                      </SoftTypography>
                                    }
                                  />
                                </SoftBox>
                              ))}
                          </SoftBox>
                        )}
                      </SoftBox>
                    </Card>
                  </>
                );
              })}
            </SoftBox>
            {saveError && (
              <SoftBox mt={2} mb={1} p={2} sx={{
                border: "1px solid #B42318", borderRadius: "8px", backgroundColor: "#FEF3F2",
              }}>
                <SoftTypography variant="body2" fontWeight="bold" color="error">
                  Your answers were not saved
                </SoftTypography>
                <SoftTypography variant="body2" color="dark" mt={0.5}>
                  {saveError}
                </SoftTypography>
              </SoftBox>
            )}
            <SoftBox mt={1} mb={2}>
              <SoftBox mt={2} mb={1}>
                <SoftTypography variant="body2" fontWeight="medium" color="dark">
                  Please answer all questions above to continue
                </SoftTypography>
              </SoftBox>
            </SoftBox>
            <SoftBox mt={1} mb={2}>
              <SoftBox mt={2} mb={1}>
                <SoftButton
                  variant="contained"
                  color="gt_primary"
                  onClick={submit}
                  fullWidth
                  disabled={isSubmitting || questions.some((q, index) =>
                    q.type === "MULTI_SELECT"
                      ? !selectedAnswers[index] ||
                        selectedAnswers[index].length === 0
                      : !selectedAnswers[index]
                  )}
                  sx={{
                    "&.Mui-disabled": {
                      opacity: "0.6",
                    },
                  }}
                >
                  {isSubmitting
                    ? "Saving\u2026"
                    : nextSectionName === "Finish" ? "Submit" : "Next"}
                </SoftButton>
              </SoftBox>
            </SoftBox>
          </Container>
        </>
      );
    }
  };

  const handleMultiAnswer = (e, value, index) => {
    const newAnswers = [...selectedAnswers];
    newAnswers[index] = newAnswers[index] || [];
    if (e.target.checked) {
      newAnswers[index] = [...newAnswers[index], value];
    } else {
      newAnswers[index] = newAnswers[index].filter((c) => c !== value);
    }
    setSelectedAnswers(newAnswers);
  };

  return <Container sx={{padding: "0px !important", margin: "-30px auto 20px auto !important"}}>{renderGrammer()}</Container>;
}
